import { useState } from 'react';

const EMPTY_POS = { name: '', addr: '' };

export default function StepCustomer({ data, onChange, onNext }) {
  const [errors, setErrors] = useState({});
  const [shippingSame, setShippingSame] = useState(true);

  const set = (key, val) => onChange({ ...data, [key]: val });
  const setBilling = (key, val) => onChange({ ...data, billing: { ...(data.billing || {}), [key]: val } });
  const setShipping = (key, val) => onChange({ ...data, shipping: { ...(data.shipping || {}), [key]: val } });

  const setPos = (i, field, val) => {
    const pos = [...(data.pos || [{ ...EMPTY_POS }])];
    pos[i] = { ...pos[i], [field]: val };
    onChange({ ...data, pos });
  };

  const addPos = () => onChange({ ...data, pos: [...(data.pos || []), { ...EMPTY_POS }] });

  const removePos = (i) => {
    const pos = (data.pos || []).filter((_, idx) => idx !== i);
    onChange({ ...data, pos });
  };

  const validate = () => {
    const e = {};
    if (!data.custName?.trim()) e.custName = 'Required';
    if (!data.custUen?.trim()) e.custUen = 'Required';
    if (!data.custEmail?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.custEmail))
      e.custEmail = 'Enter a valid email address';
    if (!data.custPhone?.trim()) e.custPhone = 'Required';
    if (!data.billing?.street1?.trim()) e.street1 = 'Required';
    if (!data.billing?.country?.trim()) e.country = 'Required';
    if (!shippingSame) {
      if (!data.shipping?.street1?.trim()) e.shipStreet1 = 'Required';
      if (!data.shipping?.country?.trim()) e.shipCountry = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    // If shipping same as billing, copy billing into shipping before proceeding
    if (shippingSame) {
      onChange({ ...data, shipping: { ...(data.billing || {}) } });
    }
    if (validate()) onNext();
  };

  const pos = data.pos?.length ? data.pos : [{ ...EMPTY_POS }];
  const billing = data.billing || {};
  const shipping = data.shipping || {};

  const addressFields = (values, setter, prefix = '') => (
    <>
      <div className="field-grid" style={{ marginBottom: 12 }}>
        <div className="field">
          <label>Attention</label>
          <input
            type="text"
            value={values.attention || ''}
            onChange={e => setter('attention', e.target.value)}
            placeholder="Contact person name"
          />
          {prefix === '' && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Auto-filled from signatory name if blank</div>}
        </div>
        <div className="field">
          <label>Phone</label>
          <input
            type="text"
            value={values.phone || ''}
            onChange={e => setter('phone', e.target.value)}
            placeholder="+65 9123 4567"
          />
          {prefix === '' && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Auto-filled from contact phone if blank</div>}
        </div>
      </div>
      <div className="field-grid full" style={{ marginBottom: 12 }}>
        <div className={`field${errors[prefix + 'street1'] ? ' has-error' : ''}`}>
          <label>Street 1 <span className="req">*</span></label>
          <input
            type="text"
            value={values.street1 || ''}
            onChange={e => setter('street1', e.target.value)}
            placeholder="e.g. 10 Orchard Road #05-01"
          />
          {errors[prefix + 'street1'] && <div className="field-error">{errors[prefix + 'street1']}</div>}
        </div>
      </div>
      <div className="field-grid full" style={{ marginBottom: 12 }}>
        <div className="field">
          <label>Street 2</label>
          <input
            type="text"
            value={values.street2 || ''}
            onChange={e => setter('street2', e.target.value)}
            placeholder="Building name (optional)"
          />
        </div>
      </div>
      <div className="field-grid three">
        <div className="field">
          <label>City</label>
          <input
            type="text"
            value={values.city || 'Singapore'}
            onChange={e => setter('city', e.target.value)}
          />
        </div>
        <div className="field">
          <label>Zip / Postal code</label>
          <input
            type="text"
            value={values.zip || ''}
            onChange={e => setter('zip', e.target.value)}
            placeholder="e.g. 238880"
            maxLength={6}
          />
        </div>
        <div className={`field${errors[prefix + 'country'] ? ' has-error' : ''}`}>
          <label>Country <span className="req">*</span></label>
          <select
            value={values.country || 'Singapore'}
            onChange={e => setter('country', e.target.value)}
          >
            <option value="Singapore">Singapore</option>
            <option value="Malaysia">Malaysia</option>
            <option value="Indonesia">Indonesia</option>
            <option value="Thailand">Thailand</option>
            <option value="Philippines">Philippines</option>
            <option value="Vietnam">Vietnam</option>
            <option value="Other">Other</option>
          </select>
          {errors[prefix + 'country'] && <div className="field-error">{errors[prefix + 'country']}</div>}
        </div>
      </div>
    </>
  );

  return (
    <div>
      {/* ── Customer details ─────────────────────────────────── */}
      <div className="card">
        <div className="card-title">
          Customer details
          <span className="badge">* required</span>
        </div>
        <div className="field-grid">
          <div className={`field${errors.custName ? ' has-error' : ''}`}>
            <label>Company name <span className="req">*</span></label>
            <input
              type="text"
              value={data.custName || ''}
              onChange={e => set('custName', e.target.value)}
              placeholder="e.g. Glow Aesthetics Pte. Ltd."
            />
            {errors.custName && <div className="field-error">{errors.custName}</div>}
          </div>
          <div className={`field${errors.custUen ? ' has-error' : ''}`}>
            <label>UEN / Registration no. <span className="req">*</span></label>
            <input
              type="text"
              value={data.custUen || ''}
              onChange={e => set('custUen', e.target.value)}
              placeholder="e.g. 202512345A"
            />
            {errors.custUen && <div className="field-error">{errors.custUen}</div>}
          </div>
          <div className={`field${errors.custEmail ? ' has-error' : ''}`}>
            <label>Signatory email <span className="req">*</span></label>
            <input
              type="email"
              value={data.custEmail || ''}
              onChange={e => set('custEmail', e.target.value)}
              placeholder="e.g. jane@clinic.sg"
            />
            {errors.custEmail && <div className="field-error">{errors.custEmail}</div>}
          </div>
          <div className={`field${errors.custPhone ? ' has-error' : ''}`}>
            <label>Phone <span className="req">*</span></label>
            <input
              type="text"
              value={data.custPhone || ''}
              onChange={e => set('custPhone', e.target.value)}
              placeholder="e.g. +65 9123 4567"
            />
            {errors.custPhone && <div className="field-error">{errors.custPhone}</div>}
          </div>
          <div className="field">
            <label>Signatory name</label>
            <input
              type="text"
              value={data.custSigName || ''}
              onChange={e => set('custSigName', e.target.value)}
              placeholder="e.g. Jane Tan"
            />
          </div>
          <div className="field">
            <label>Signatory title</label>
            <input
              type="text"
              value={data.custSigTitle || ''}
              onChange={e => set('custSigTitle', e.target.value)}
              placeholder="e.g. Director"
            />
          </div>
        </div>
      </div>

      {/* ── Billing address ─────────────────────────────────── */}
      <div className="card">
        <div className="card-title">
          Billing address
          <span className="badge">maps to Zoho billing &amp; shipping</span>
        </div>
        {addressFields(billing, setBilling, '')}
      </div>

      {/* ── Shipping address toggle ──────────────────────────── */}
      <div className="card">
        <div className="card-title">Shipping address</div>

        {/* Checkbox: same as billing */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: shippingSame ? 0 : 16 }}>
          <input
            type="checkbox"
            checked={shippingSame}
            onChange={e => setShippingSame(e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--ink)' }}
          />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Same as billing address</span>
        </label>

        {!shippingSame && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            {addressFields(shipping, setShipping, 'ship')}
          </div>
        )}
      </div>

      {/* ── Points of sale ───────────────────────────────────── */}
      <div className="card">
        <div className="card-title">
          Points of sale
          <span className="badge optional">optional</span>
        </div>
        {pos.map((p, i) => (
          <div key={i} className="pos-row">
            <div className="field">
              {i === 0 && <label>Point of sale name</label>}
              <input
                type="text"
                value={p.name}
                onChange={e => setPos(i, 'name', e.target.value)}
                placeholder={`e.g. ${i === 0 ? 'Main Clinic' : 'Second Outlet'}`}
              />
            </div>
            <div className="field">
              {i === 0 && <label>Address</label>}
              <input
                type="text"
                value={p.addr}
                onChange={e => setPos(i, 'addr', e.target.value)}
                placeholder="Full address"
              />
            </div>
            {i > 0 && (
              <button className="pos-remove" onClick={() => removePos(i)} title="Remove">×</button>
            )}
          </div>
        ))}
        <button className="btn btn-ghost btn-sm" onClick={addPos} style={{ marginTop: 4 }}>
          + Add another point of sale
        </button>
      </div>

      <div className="row end">
        <button className="btn btn-primary" onClick={handleNext}>Next: Terms →</button>
      </div>
    </div>
  );
}
